import React, { useState, useEffect, useRef, useCallback } from 'react';

const DEMO_CSS = `@media (max-width: 800px) { .mx-demo-sidebar { display: none !important; } }`;
function InjectDemoCSS() {
  useEffect(() => {
    const id = 'mx-demo-css';
    if (!document.getElementById(id)) {
      const s = document.createElement('style'); s.id = id; s.textContent = DEMO_CSS;
      document.head.appendChild(s);
    }
    return () => { const el = document.getElementById(id); if (el) el.remove(); };
  }, []);
  return null;
}

/* ─────────────────────────────────────────────────────────────────────────────
   ANIMATION HELPERS
───────────────────────────────────────────────────────────────────────────── */
function typeAt(text, elapsed, start, speed = 32) {
  if (elapsed < start) return '';
  const n = Math.floor((elapsed - start) / speed);
  return text.slice(0, Math.min(n, text.length));
}

function slideAt(elapsed, start, dur = 380) {
  if (elapsed < start) return { opacity: 0, transform: 'translateY(14px)' };
  const p = Math.min(1, (elapsed - start) / dur);
  const e = 1 - Math.pow(1 - p, 3);
  return { opacity: e, transform: `translateY(${14 * (1 - e)}px)`, transition: 'none' };
}

function fadeAt(elapsed, start, dur = 350) {
  if (elapsed < start) return 0;
  return Math.min(1, (elapsed - start) / dur);
}

function countAt(elapsed, start, dur, from, to) {
  if (elapsed < start) return from;
  const p = Math.min(1, (elapsed - start) / dur);
  const e = 1 - Math.pow(1 - p, 2);
  return Math.round(from + (to - from) * e);
}

function barAt(elapsed, start, dur) {
  if (elapsed < start) return 0;
  return Math.min(100, ((elapsed - start) / dur) * 100);
}

/* ─────────────────────────────────────────────────────────────────────────────
   SHARED DEMO SHELL — play/pause, progress bar, loop
───────────────────────────────────────────────────────────────────────────── */
function DemoShell({ duration, children, title, accent = '#ef4444', subtitle }) {
  const [elapsed, setElapsed] = useState(0);
  const [playing, setPlaying] = useState(true);
  const rafRef  = useRef(null);
  const lastRef = useRef(null);
  const elapsedRef = useRef(0);

  const tick = useCallback((ts) => {
    if (lastRef.current !== null) {
      const delta = ts - lastRef.current;
      elapsedRef.current = elapsedRef.current + delta;
      if (elapsedRef.current >= duration) elapsedRef.current = 0;
      setElapsed(elapsedRef.current);
    }
    lastRef.current = ts;
    rafRef.current = requestAnimationFrame(tick);
  }, [duration]);

  useEffect(() => {
    if (!playing) { lastRef.current = null; return; }
    rafRef.current = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(rafRef.current); lastRef.current = null; };
  }, [playing, tick]);

  function seek(e) {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    elapsedRef.current = Math.floor(ratio * duration);
    setElapsed(elapsedRef.current);
  }

  const pct = (elapsed / duration) * 100;

  return (
    <div style={{
      width: '100%', background: '#060c16', borderRadius: 14, overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
      fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
      boxShadow: '0 32px 80px rgba(0,0,0,0.6)',
    }}>
      <InjectDemoCSS />
      {/* Content */}
      <div style={{ flex: 1, position: 'relative', minHeight: 340 }}>
        {children(elapsed)}

        {/* LIVE DEMO badge */}
        <div style={{
          position: 'absolute', top: 14, right: 14,
          background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)',
          border: `1px solid ${accent}40`, borderRadius: 20,
          padding: '4px 10px', fontSize: 10, fontWeight: 700,
          color: accent, letterSpacing: 1, textTransform: 'uppercase',
          display: 'flex', alignItems: 'center', gap: 5,
        }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: accent, display: 'inline-block', animation: 'glowPulse 2s ease infinite' }} />
          LIVE DEMO
        </div>
      </div>

      {/* Controls */}
      <div style={{ background: '#0a1020', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        {/* Progress bar */}
        <div
          onClick={seek}
          style={{ height: 3, background: '#1e293b', cursor: 'pointer', position: 'relative' }}
        >
          <div style={{
            position: 'absolute', left: 0, top: 0, bottom: 0,
            width: `${pct}%`, background: `linear-gradient(90deg, ${accent}, ${accent}bb)`,
            boxShadow: `0 0 8px ${accent}80`,
          }} />
        </div>
        <div style={{ padding: '7px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={() => setPlaying(p => !p)}
            style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 14, padding: 0, fontFamily: 'monospace' }}
          >
            {playing ? '||' : '▶'}
          </button>
          <div style={{ flex: 1 }}>
            <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>{title}</span>
            {subtitle && <span style={{ fontSize: 11, color: '#334155', marginLeft: 8 }}>{subtitle}</span>}
          </div>
          <button
            onClick={() => { elapsedRef.current = 0; setElapsed(0); }}
            style={{ background: 'transparent', border: 'none', color: '#334155', cursor: 'pointer', fontSize: 12, padding: 0 }}
            title="Restart"
          >↺</button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   DEMO 1 — Platform Overview: full lead qualification loop (15 000 ms)
───────────────────────────────────────────────────────────────────────────── */
export function PlatformOverviewDemo() {
  const T = 15000;

  return (
    <DemoShell duration={T} title="WhatsApp AI Agent — Platform Overview" subtitle="Lead capture · Follow-up · Booking" accent="#ef4444">
      {(e) => {
        const msgs = [
          { t: 600,  role: 'in',  text: 'Hi! I\'m interested in your Data Science course' },
          { t: 2200, role: 'out', text: 'Hello! I\'m Alex, your AI counselor.\nWhich Data Science track — 3-month intensive or 6-month full-stack?' },
          { t: 5200, role: 'in',  text: '6-month one. What\'s the fee?' },
          { t: 6800, role: 'out', text: '₹45,000 for 6 months · EMI ₹7,500/mo\n94% placement rate · Starts May 15\n\nWant to book a free counseling call?' },
          { t: 9800, role: 'in',  text: 'Yes! Tomorrow morning works?' },
          { t: 11200, role: 'out', text: 'Booked for tomorrow 10 AM!\nYou\'ll get a WhatsApp reminder 1 hr before.' },
        ];
        const showTyping = (e >= 1400 && e < 2200) || (e >= 5800 && e < 6800) || (e >= 10400 && e < 11200);

        const activities = [
          { t: 600,   label: 'Conversation started',      color: '#64748b' },
          { t: 2200,  label: 'AI responded in 0.4s',      color: '#ef4444' },
          { t: 5000,  label: 'Lead detected & captured',  color: '#f59e0b' },
          { t: 6800,  label: 'Intent: Enroll · Qualified', color: '#22c55e' },
          { t: 9800,  label: 'Booking intent detected',   color: '#dc2626' },
          { t: 11200, label: 'Appointment scheduled',     color: '#4ade80' },
          { t: 12000, label: '1 lead · 1 booking · 0 effort', color: '#ef4444' },
        ];

        return (
          <div style={{ display: 'flex', height: '100%', minHeight: 340 }}>
            {/* Left: chat */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '14px 16px', borderRight: '1px solid rgba(255,255,255,0.05)' }}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'linear-gradient(135deg, #ef4444, #b91c1c)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color: '#fff', flexShrink: 0, letterSpacing: 0.5 }}>AI</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#f1f5f9' }}>MaxxBot — Alex</div>
                  <div style={{ fontSize: 10, color: '#22c55e', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
                    Online · 24/7 Active
                  </div>
                </div>
                <div style={{ marginLeft: 'auto', fontSize: 10, color: '#334155', background: '#1e293b', padding: '3px 8px', borderRadius: 10 }}>
                  WhatsApp
                </div>
              </div>

              {/* Messages */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8, overflowY: 'hidden' }}>
                {msgs.map((m, i) => {
                  const s = slideAt(e, m.t);
                  if (e < m.t) return null;
                  return (
                    <div key={i} style={{ display: 'flex', justifyContent: m.role === 'out' ? 'flex-end' : 'flex-start', ...s }}>
                      <div style={{
                        maxWidth: '82%', padding: '8px 12px', fontSize: 12, lineHeight: 1.6, whiteSpace: 'pre-line',
                        borderRadius: m.role === 'out' ? '14px 3px 14px 14px' : '3px 14px 14px 14px',
                        background: m.role === 'out' ? 'linear-gradient(135deg, #ef4444, #dc2626)' : '#1e293b',
                        color: m.role === 'out' ? '#fff' : '#f1f5f9',
                        fontWeight: m.role === 'out' ? 500 : 400,
                      }}>
                        {m.text}
                      </div>
                    </div>
                  );
                })}
                {showTyping && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', animation: 'slideInMsg 0.3s ease' }}>
                    <div style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)', padding: '9px 14px', borderRadius: '14px 3px 14px 14px', display: 'flex', gap: 3, alignItems: 'center' }}>
                      {[0, 0.15, 0.3].map((d, i) => (
                        <span key={i} style={{ width: 5, height: 5, borderRadius: '50%', background: 'rgba(255,255,255,0.8)', display: 'inline-block', animation: `typingBounce 0.9s ease ${d}s infinite` }} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right: activity log */}
            <div className="mx-demo-sidebar" style={{ width: 210, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#334155', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>Live Activity</div>
              {activities.map((a, i) => {
                if (e < a.t) return null;
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, ...slideAt(e, a.t, 300) }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: a.color, flexShrink: 0, display: 'inline-block' }} />
                    <span style={{ fontSize: 11, color: a.color, fontWeight: 600, lineHeight: 1.3 }}>{a.label}</span>
                  </div>
                );
              })}

              {/* Stats */}
              {e >= 12500 && (
                <div style={{ marginTop: 'auto', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 10, padding: '10px 12px', ...slideAt(e, 12500) }}>
                  {[['Messages', '6'], ['Lead Captured', '1'], ['Bookings', '1'], ['Response time', '0.4s']].map(([lab, val]) => (
                    <div key={lab} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 0' }}>
                      <span style={{ fontSize: 11, color: '#64748b' }}>{lab}</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#ef4444' }}>{val}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      }}
    </DemoShell>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   DEMO 2 — AI + Integrations: calendar · HubSpot · web search (17 000 ms)
───────────────────────────────────────────────────────────────────────────── */
export function IntegrationsDemoVideo() {
  const T = 17000;

  const integrations = [
    { abbr: 'CAL', name: 'Google Calendar', color: '#4285f4' },
    { abbr: 'CRM', name: 'HubSpot CRM',     color: '#ff7a59' },
    { abbr: 'WEB', name: 'Web Search',       color: '#22c55e' },
  ];

  const script = [
    { t: 500,   role: 'in',   text: 'Do I have any meetings after 3 PM today?' },
    { t: 2000,  role: 'tool', text: 'Querying Google Calendar…', color: '#4285f4' },
    { t: 3600,  role: 'out',  text: 'After 3 PM today:\n— 3:30 PM · Product Review (45 min)\n— 5:00 PM · Team Sync (30 min)\n\nNext free slot: 6 PM onwards' },
    { t: 6000,  role: 'in',   text: 'Find Arjun Nair\'s contact in our CRM' },
    { t: 7200,  role: 'tool', text: 'Querying HubSpot CRM…', color: '#ff7a59' },
    { t: 8800,  role: 'out',  text: 'Arjun Nair (ID: 48291)\narjun@healthfirst.in\n+91 99887 76655\nStage: SQL · Last contact: 3d ago' },
    { t: 11000, role: 'in',   text: 'What\'s the Sensex level right now?' },
    { t: 12200, role: 'tool', text: 'Searching the web…', color: '#22c55e' },
    { t: 13800, role: 'out',  text: 'Sensex: 74,245.30 (+0.82%)\nNifty 50: 22,512.15 (+0.76%)\nAs of 2:47 PM IST · Source: NSE' },
    { t: 15500, role: 'sys',  text: '3 tools · 3 queries · 0 manual lookups' },
  ];

  const toolCalls = [
    { t: 2000, end: 3600, tool: 0 },
    { t: 7200, end: 8800, tool: 1 },
    { t: 12200, end: 13800, tool: 2 },
  ];

  return (
    <DemoShell duration={T} title="AI Agent + Live Integrations" subtitle="Calendar · HubSpot CRM · Web Search" accent="#ef4444">
      {(e) => {
        const showTyping = (e >= 1200 && e < 2000) || (e >= 6600 && e < 7200) || (e >= 11600 && e < 12200);

        return (
          <div style={{ display: 'flex', height: '100%', minHeight: 340 }}>
            {/* Chat */}
            <div style={{ flex: 1, padding: '14px 16px', display: 'flex', flexDirection: 'column', borderRight: '1px solid rgba(255,255,255,0.05)' }}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'linear-gradient(135deg, #ef4444, #b91c1c)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color: '#fff', letterSpacing: 0.5 }}>AI</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#f1f5f9' }}>MaxxBot — Integrated</div>
                  <div style={{ fontSize: 10, color: '#22c55e' }}>3 tools connected · Real-time data</div>
                </div>
              </div>

              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8, overflowY: 'hidden' }}>
                {script.map((m, i) => {
                  if (e < m.t) return null;
                  if (m.role === 'sys') {
                    return (
                      <div key={i} style={{ textAlign: 'center', ...slideAt(e, m.t) }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#ef4444', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', padding: '4px 14px', borderRadius: 20 }}>{m.text}</span>
                      </div>
                    );
                  }
                  if (m.role === 'tool') {
                    return (
                      <div key={i} style={{ display: 'flex', justifyContent: 'center', ...slideAt(e, m.t, 250) }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: m.color, background: `${m.color}14`, border: `1px solid ${m.color}30`, padding: '4px 14px', borderRadius: 20, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: m.color, display: 'inline-block', animation: 'glowPulse 1s ease infinite' }} />
                          {m.text}
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div key={i} style={{ display: 'flex', justifyContent: m.role === 'out' ? 'flex-end' : 'flex-start', ...slideAt(e, m.t) }}>
                      <div style={{
                        maxWidth: '84%', padding: '8px 12px', fontSize: 12, lineHeight: 1.6, whiteSpace: 'pre-line',
                        borderRadius: m.role === 'out' ? '14px 3px 14px 14px' : '3px 14px 14px 14px',
                        background: m.role === 'out' ? 'linear-gradient(135deg, #ef4444, #dc2626)' : '#1e293b',
                        color: '#f1f5f9', fontWeight: m.role === 'out' ? 500 : 400,
                      }}>
                        {m.text}
                      </div>
                    </div>
                  );
                })}
                {showTyping && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <div style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)', padding: '9px 14px', borderRadius: '14px 3px 14px 14px', display: 'flex', gap: 3, alignItems: 'center' }}>
                      {[0, 0.15, 0.3].map((d, i) => (
                        <span key={i} style={{ width: 5, height: 5, borderRadius: '50%', background: 'rgba(255,255,255,0.8)', display: 'inline-block', animation: `typingBounce 0.9s ease ${d}s infinite` }} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right: tools panel */}
            <div className="mx-demo-sidebar" style={{ width: 190, padding: '14px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#334155', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 2 }}>Active Tools</div>
              {integrations.map((t, i) => {
                const active = toolCalls.find(tc => tc.tool === i && e >= tc.t && e < tc.end);
                const done   = toolCalls.find(tc => tc.tool === i && e >= tc.end);
                return (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    background: active ? `${t.color}18` : done ? `${t.color}08` : 'rgba(255,255,255,0.02)',
                    border: `1px solid ${active ? t.color + '50' : done ? t.color + '25' : 'rgba(255,255,255,0.06)'}`,
                    borderRadius: 10, padding: '8px 10px', transition: 'all 0.3s',
                  }}>
                    <span style={{ fontSize: 9, fontWeight: 800, color: active || done ? t.color : '#475569', letterSpacing: 0.5 }}>{t.abbr}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: active || done ? t.color : '#475569' }}>{t.name}</div>
                      <div style={{ fontSize: 10, color: '#334155' }}>
                        {active ? '⟳ Running…' : done ? '✓ Connected' : 'Standby'}
                      </div>
                    </div>
                  </div>
                );
              })}

              {e >= 15500 && (
                <div style={{ marginTop: 'auto', ...slideAt(e, 15500) }}>
                  {[['Total queries', '3'], ['Tools called', '3'], ['Manual work', '0']].map(([l, v]) => (
                    <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                      <span style={{ fontSize: 11, color: '#475569' }}>{l}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#ef4444' }}>{v}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      }}
    </DemoShell>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   DEMO 3 — Broadcaster Campaign: compose → send → analytics (15 000 ms)
───────────────────────────────────────────────────────────────────────────── */
export function BroadcasterDemo() {
  const T = 15000;

  const msgFull = `Exclusive Diwali Offer — Limited Time!

Hi {name}, here's something special:

30% OFF on all programs
Valid until Oct 25, 2026
Placement guaranteed or full refund

Claim now — bit.ly/diwali2026

Reply STOP to opt out.`;

  return (
    <DemoShell duration={T} title="WhatsApp Broadcaster — Campaign Demo" subtitle="Compose · Send · Analyse" accent="#b91c1c">
      {(e) => {
        const msgTyped = typeAt(msgFull, e, 800, 28);
        const contactsShown = e >= 4200;
        const contactCount  = contactsShown ? countAt(e, 4200, 1400, 0, 25000) : 0;
        const sendClicked   = e >= 6800;
        const progress      = sendClicked ? barAt(e, 7000, 2800) : 0;
        const done          = e >= 9800;
        const showStats     = e >= 10200;
        const delivered     = showStats ? countAt(e, 10200, 1200, 0, 24213) : 0;
        const opened        = showStats ? countAt(e, 10800, 1000, 0, 8765)  : 0;
        const clicked       = showStats ? countAt(e, 11400, 900, 0, 3241)   : 0;
        const replied       = showStats ? countAt(e, 12000, 800, 0, 891)    : 0;

        return (
          <div style={{ display: 'flex', height: '100%', minHeight: 340 }}>
            {/* Left: composer */}
            <div style={{ flex: 1, padding: '14px 16px', borderRight: '1px solid rgba(255,255,255,0.05)', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {/* Campaign header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 34, height: 34, borderRadius: 10, background: 'linear-gradient(135deg, #b91c1c, #7f1d1d)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, color: '#fff', letterSpacing: 0.5 }}>BC</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#f1f5f9' }}>Diwali 2026 Campaign</div>
                  <div style={{ fontSize: 10, color: '#b91c1c' }}>WhatsApp Broadcaster</div>
                </div>
                {done && (
                  <div style={{ marginLeft: 'auto', background: '#14532d', border: '1px solid #166534', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, color: '#4ade80', ...slideAt(e, 9800) }}>
                    ✓ Sent
                  </div>
                )}
              </div>

              {/* Message preview */}
              <div style={{ flex: 1, background: '#0f172a', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: 12, position: 'relative', overflow: 'hidden' }}>
                <div style={{ fontSize: 10, color: '#334155', marginBottom: 6, fontWeight: 600, letterSpacing: 0.5 }}>MESSAGE PREVIEW</div>
                <pre style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.6, margin: 0, fontFamily: 'inherit', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {msgTyped}
                  {e >= 800 && e < 4600 && msgTyped.length < msgFull.length && (
                    <span style={{ display: 'inline-block', width: 1, height: '1em', background: '#b91c1c', animation: 'glowPulse 0.8s ease infinite', verticalAlign: 'bottom', marginLeft: 1 }} />
                  )}
                </pre>
              </div>

              {/* Contact count */}
              {contactsShown && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#0f172a', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8, padding: '8px 12px', ...slideAt(e, 4200) }}>
                  <span style={{ fontSize: 12, color: '#94a3b8' }}>Recipients:</span>
                  <span style={{ fontSize: 14, fontWeight: 800, color: '#f1f5f9', marginLeft: 4 }}>{contactCount.toLocaleString()}</span>
                  {contactCount >= 25000 && <span style={{ fontSize: 11, color: '#22c55e', marginLeft: 'auto' }}>✓ Ready</span>}
                </div>
              )}

              {/* Send button / progress */}
              {e >= 6000 && (
                <div style={slideAt(e, 6000)}>
                  {!sendClicked ? (
                    <div style={{
                      background: 'linear-gradient(90deg, #b91c1c, #ef4444)',
                      color: '#fff', fontWeight: 700, fontSize: 14,
                      padding: '11px', borderRadius: 10, textAlign: 'center',
                      cursor: 'pointer', boxShadow: '0 6px 24px rgba(185,28,28,0.4)',
                      animation: 'glowPulse 1.5s ease infinite',
                    }}>
                      Send to 25,000 contacts →
                    </div>
                  ) : (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 11, color: '#64748b' }}>Sending…</span>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#b91c1c' }}>{Math.round(progress)}%</span>
                      </div>
                      <div style={{ height: 6, background: '#1e293b', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{
                          height: '100%', width: `${progress}%`,
                          background: 'linear-gradient(90deg, #b91c1c, #ef4444)',
                          boxShadow: '0 0 8px rgba(239,68,68,0.6)',
                          transition: 'width 0.1s linear',
                          borderRadius: 3,
                        }} />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right: analytics */}
            <div className="mx-demo-sidebar" style={{ width: 200, padding: '14px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#334155', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 2 }}>Analytics</div>

              {showStats ? (
                <>
                  {[
                    { label: 'Delivered', val: delivered, total: 25000, color: '#22c55e' },
                    { label: 'Opened',    val: opened,   total: 24213, color: '#ef4444' },
                    { label: 'Clicked',   val: clicked,  total: 24213, color: '#f59e0b' },
                    { label: 'Replied',   val: replied,  total: 24213, color: '#b91c1c' },
                  ].map((s, i) => (
                    <div key={i} style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${s.color}20`, borderRadius: 10, padding: '10px 12px', ...slideAt(e, 10200 + i * 400) }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                        <span style={{ fontSize: 11, color: '#64748b' }}>{s.label}</span>
                        <span style={{ fontSize: 12, fontWeight: 800, color: s.color }}>
                          {s.total > 0 ? ((s.val / s.total) * 100).toFixed(1) : 0}%
                        </span>
                      </div>
                      <div style={{ height: 4, background: '#1e293b', borderRadius: 2, overflow: 'hidden' }}>
                        <div style={{
                          height: '100%', width: `${s.total > 0 ? (s.val / s.total) * 100 : 0}%`,
                          background: s.color, borderRadius: 2,
                        }} />
                      </div>
                      <div style={{ fontSize: 10, color: '#334155', marginTop: 3 }}>{s.val.toLocaleString()}</div>
                    </div>
                  ))}

                  {e >= 13000 && (
                    <div style={{ marginTop: 4, padding: '8px 10px', background: 'rgba(185,28,28,0.12)', border: '1px solid rgba(185,28,28,0.25)', borderRadius: 8, textAlign: 'center', ...slideAt(e, 13000) }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#f87171' }}>Top Campaign</div>
                      <div style={{ fontSize: 10, color: '#64748b' }}>36.2% open rate</div>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#334155', fontSize: 12 }}>
                  {e >= 6800 ? 'Sending…' : 'Configure campaign →'}
                </div>
              )}
            </div>
          </div>
        );
      }}
    </DemoShell>
  );
}
